import React, { Component } from 'react';

import classnames from 'classnames';
import { Book, HelpCircle, Lightbulb, LogOut, MessagesSquare, Plus, Settings } from 'lucide-react';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { reloadCompany } from 'common/actions/company';
import AJAX from 'common/AJAX';
import Tooltip from 'common/common/Tooltip';
import UserAvatarV2 from 'common/common/UserAvatarV2';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import connect from 'common/core/connect';
import ErrorMessage from 'common/ErrorMessage';
import { isMobile } from 'common/hooks/useMedia';
import LazyLoadedImage from 'common/LazyLoadedImage';
import Link from 'common/Link';
import DefaultCompanyRoleNames, { hasRoleName } from 'common/roles/CompanyRoleNames';
import AdminJoinBetaModal from 'common/subdomain/admin/v2/modals/AdminJoinBetaModal';
import Tappable from 'common/Tappable';
import Popover from 'common/ui/Popover';
import { P } from 'common/ui/Text';
import Button from 'common/uiV3/Button';
import Dialog from 'common/uiV3/Dialog';
import Field from 'common/uiV3/Field';
import { Select } from 'common/uiV3/Select';
import Textarea from 'common/uiV3/Textarea';
import devURL from 'common/util/devURL';
import isPro from 'common/util/isPro';
import isServer from 'common/util/isServer';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';
import { findRoleForUser } from 'common/util/permissions';
import withContexts from 'common/util/withContexts';

import 'css/components/subdomain/admin/_AdminNavAccountMenu.scss';

import CannyIcon from 'img/canny-icon.svg';

const IDBJoinBetaQueryParam = 'joinBeta';

const isLeaveFormValid = (leaveForm) => {
  if (!leaveForm) {
    return false;
  } else if (!leaveForm.reason) {
    return false;
  } else if (leaveForm.reason === 'other') {
    return leaveForm.comment && leaveForm.comment.trim() !== '';
  }

  return true;
};

class AdminNavAccountMenu extends Component {
  static propTypes = {
    menuPosition: PropTypes.oneOf(['right', 'bottom']),
    company: PropTypes.shape({
      featureAllowlist: PropTypes.arrayOf(PropTypes.string),
      members: PropTypes.array,
      roles: PropTypes.array,
    }),
    viewer: PropTypes.shape({
      companies: PropTypes.array,
    }),
  };

  static defaultProps = {
    menuPosition: 'right',
  };

  state = {
    isMenuOpen: false,
    modal: null,
    loading: false,
    error: null,
  };

  onLogoutTapped = async () => {
    const response = await AJAX.post('/api/viewer/logout', {});
    if (response !== 'success') {
      return;
    }

    if (window.Intercom) {
      window.Intercom('shutdown');
    }
    document.location.href = devURL('https://canny.io');
  };

  closeBetaModals = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete(IDBJoinBetaQueryParam);
    window.history.replaceState({}, '', url.toString());

    this.setState({
      modal: null,
      leaveForm: null,
      loading: false,
      error: null,
    });
  };

  onLeaveBeta = async () => {
    const { leaveForm } = this.state;
    const { reloadCompany } = this.props;

    this.setState({ loading: true, error: null });

    const response = await AJAX.post('/api/ideas/beta', {
      enabled: false,
      reason: leaveForm.reason,
      feedback: leaveForm.comment,
    });

    const { error } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
    });

    if (error) {
      this.setState({ error: error.message, loading: false });
      return;
    }

    await reloadCompany();
    this.closeBetaModals();
  };

  renderUserProfile() {
    const { viewer, menuPosition } = this.props;
    if (!viewer || !viewer._id) {
      return null;
    }

    return (
      <Popover
        open={this.state.isMenuOpen}
        onOpenChange={(open) => this.setState({ isMenuOpen: open })}>
        {viewer?._id && (
          <Popover.Trigger asChild>
            <button className="unstyled-button">
              <UserAvatarV2 user={viewer} />
            </button>
          </Popover.Trigger>
        )}
        <Popover.Content
          align="end"
          side={isMobile() ? 'bottom' : menuPosition}
          sideOffset={12}
          className="adminNavAccountMenuPortal p-0">
          {this.renderAccountMenu()}
        </Popover.Content>
      </Popover>
    );
  }

  renderCompanies() {
    const {
      viewer: { companies },
    } = this.props;

    const items = [];
    if (companies.length > 1) {
      companies.forEach((company) => {
        const companyLogo = company.logoURL;
        const companyURL =
          company.integrationAuthRedirectionURL ??
          devURL('https://' + company.subdomain + '.canny.io/admin');
        items.push(
          <a key={company._id} className="company menuItem" href={companyURL}>
            <LazyLoadedImage
              className="companyLogo"
              src={companyLogo ?? CannyIcon}
              alt=""></LazyLoadedImage>
            <P className="itemName">{company.name}</P>
          </a>,
        );
      });
    }

    const newCompanyURL = devURL('https://canny.io/register');
    return (
      <div className="companies section">
        <div className="sectionTitleWrapper">
          <p className="sectionTitle">Workspaces</p>
          <Tooltip
            className="newCompanyLinkTooltip"
            delay={300}
            position="left"
            value={'Create a new workspace'}>
            <a href={newCompanyURL} className="newCompanyLink" aria-label="Create a new workspace">
              <Plus size={14} />
            </a>
          </Tooltip>
        </div>
        {items}
      </div>
    );
  }

  getIDBBetaFlags() {
    const { company } = this.props;
    const isIDBEnabled = company?.featureAllowlist?.includes('idea-database');
    const viewerRole = this.props.viewer && findRoleForUser(company, this.props.viewer);
    const isOwner = viewerRole && hasRoleName(DefaultCompanyRoleNames.owner)(viewerRole);
    const canEnableIDB =
      company && !isIDBEnabled && isOwner && isPro(company.billingData?.plan?.planID);
    const canDisableIDB = company && isIDBEnabled && isOwner;

    return { canEnableIDB, canDisableIDB };
  }

  renderAccountMenu() {
    const {
      viewer: { companies },
    } = this.props;

    const { canEnableIDB, canDisableIDB } = this.getIDBBetaFlags();

    return (
      <>
        <div
          className={classnames('menuItems adminMenu', {
            shrink: companies.length <= 1,
          })}>
          {this.renderCompanies()}
          <div className="section spacedOut">
            <a
              className="menuItem"
              href={devURL('https://developers.canny.io/install')}
              rel="noopener"
              target="_blank">
              <Book size={16} className="lucide-icon" />
              <P className="itemName">Docs</P>
            </a>
            <a className="menuItem" href="https://help.canny.io/" rel="noopener" target="_blank">
              <HelpCircle size={16} className="lucide-icon" />
              <P className="itemName">Help center</P>
            </a>
            <a
              className="menuItem"
              href={devURL('https://feedback.canny.io')}
              rel="noopener"
              target="_blank">
              <MessagesSquare size={16} className="lucide-icon" />
              <P className="itemName">Canny feedback</P>
            </a>
            {canDisableIDB ? (
              <Tappable
                onTap={() =>
                  this.setState({
                    modal: 'leave-idb-beta',
                  })
                }>
                <div className="menuItem">
                  <Lightbulb size={16} className="lucide-icon" />
                  <P className="itemName">Leave Ideas beta</P>
                </div>
              </Tappable>
            ) : null}
            {canEnableIDB ? (
              <Tappable
                onTap={() =>
                  this.setState({
                    modal: 'join-idb-beta',
                  })
                }>
                <div className="menuItem">
                  <Lightbulb size={16} className="lucide-icon" />
                  <P className="itemName">Join Ideas beta</P>
                </div>
              </Tappable>
            ) : null}
          </div>
          <div className="section spacedOut">
            <Link
              className="menuItem"
              onTap={() => this.setState({ isMenuOpen: false })}
              to="/admin/settings/account">
              <Settings size={16} className="lucide-icon" />
              <P className="itemName">Settings</P>
            </Link>
            <Tappable onTap={this.onLogoutTapped}>
              <div className="menuItem">
                <LogOut size={16} className="lucide-icon" />
                <P className="itemName">Logout</P>
              </div>
            </Tappable>
          </div>
        </div>
      </>
    );
  }

  render() {
    return (
      <div className="adminNavAccountMenu">
        {this.renderUserProfile()}
        {this.renderJoinBetaModal()}
        {this.renderLeaveBetaModal()}
      </div>
    );
  }

  renderJoinBetaModal() {
    const { modal } = this.state;
    const { canEnableIDB } = this.getIDBBetaFlags();
    const joinBeta =
      canEnableIDB && !isServer()
        ? new URLSearchParams(window.location.search).get(IDBJoinBetaQueryParam)
        : null;

    return (
      <AdminJoinBetaModal
        isOpen={joinBeta || modal === 'join-idb-beta'}
        onOpenChange={this.closeBetaModals}
      />
    );
  }

  renderLeaveBetaModal() {
    const { modal, leaveForm, loading } = this.state;

    const leaveReasons = [
      { value: 'not-useful', label: "It wasn't useful for our workflow" },
      { value: 'confusing', label: 'It was confusing / hard to use' },
      { value: 'no-time', label: "Don't have time to adopt right now" },
      { value: 'other', label: 'Other (please specify below)' },
    ];

    return (
      <Dialog open={modal === 'leave-idb-beta'} onOpenChange={this.closeBetaModals}>
        <Dialog.Content className="sm:max-w-[520px]">
          <Dialog.Header>
            <Dialog.Title>Leave Ideas beta</Dialog.Title>
          </Dialog.Header>
          <Dialog.Body>
            <div>
              <p>When you leave the beta:</p>
              <ul className="list-disc list-inside text-base">
                <li>You'll lose access to ideas, groups, and themes</li>
                <li>You'll affect all admins in your workspace</li>
                <li>You can rejoin the beta anytime</li>
              </ul>
            </div>
            <Field.Group>
              <Field>
                <Field.Label>Let us know why you're leaving the beta</Field.Label>
                <Field.Description>
                  Choose the reason that best matches your situation.
                </Field.Description>
                <Field.Content>
                  <Select
                    aria-label="Reason for leaving the beta"
                    value={leaveForm?.reason}
                    options={leaveReasons}
                    placeholder="Select a reason…"
                    required
                    onValueChange={(value) =>
                      this.setState({ leaveForm: { ...leaveForm, reason: value } })
                    }
                  />
                </Field.Content>
              </Field>
              <Field>
                <Field.Description>
                  Share specific feedback or suggestions for improving Ideas.
                </Field.Description>
                <Field.Content>
                  <Textarea
                    aria-label="Feedback for improving Ideas"
                    rows={3}
                    placeholder="Leave your feedback…"
                    required={leaveForm?.reason === 'other'}
                    onChange={(event) =>
                      this.setState({
                        leaveForm: { ...leaveForm, comment: event.currentTarget.value },
                      })
                    }
                  />
                </Field.Content>
              </Field>
            </Field.Group>
            {this.state.error ? <ErrorMessage>{this.state.error}</ErrorMessage> : null}
          </Dialog.Body>
          <Dialog.Footer>
            <Button onClick={this.closeBetaModals} variant="outlined">
              Cancel
            </Button>
            <Button
              isLoading={loading}
              disabled={!isLeaveFormValid(leaveForm)}
              onClick={this.onLeaveBeta}
              variant="contained"
              color="error">
              Leave beta
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    );
  }
}

export default compose(
  connect(null, { reloadCompany }),
  withContexts(
    {
      viewer: ViewerContext,
      company: CompanyContext,
    },
    {
      forwardRef: true,
    },
  ),
)(AdminNavAccountMenu);
